<template>
  <ContentTemplate :title="integrationType.title" :icon="integrationType.icon">

    <div class="d-flex">
      <div class="integration-config-template">
        <h4 class="integration-headline">Connected Workspace</h4>
        <IntegrationSkeletonLoadingComponent
            v-if="showSkeleton"></IntegrationSkeletonLoadingComponent>
        <IntegrationConfigWidget v-else
                                 :project-name="selectedWorkspace.name"
                                 :int-project-name="integrationDetail.integratedWorkspace"
                                 :date="integrationDetail.integrationCreateDate"
                                 :user="integrationDetail.integrationCreatedBy"
                                 :is-project="false"
                                 :int-type-info="integrationType"
                                 @onRemove="openConfirmationPopupForRemoveIntegration()"
                                 @onConfigure="configureIntegration()"
        />

        <template v-if="integrationType.type !== 'FIGMA' && integrationType.type !== 'GOOGLE_DRIVE'">
          <hr />
          <h4 class="integration-headline">Connected Projects</h4>
          <IntegrationSkeletonComponent
              v-if="!integrationDetail.integratedProjects || integrationDetail.integratedProjects.length == 0">
          </IntegrationSkeletonComponent>
          <template v-else>
            <IntegrationSkeletonLoadingComponent
                v-if="showSkeleton"></IntegrationSkeletonLoadingComponent>
            <IntegrationConfigWidget v-else v-for="project in integrationDetail.integratedProjects"
                                     :key="project.projectIntegrationId"
                                     :project-name="project.fpProjectName"
                                     :int-project-name="project.intProjectName"
                                     :date="project.createDate"
                                     :user="project.createdBy"
                                     :int-type-info="integrationType"
                                     @onRemove="openConfirmationPopupForRemoveProjectIntegration(project)"
            />
          </template>

          <ButtonElement @clickOnButton="addIntegration()"
                         class="btn--ms btn--accent btn--xl btn--full">
          <span>
                        <LucideIcon icon="Plus" style="margin-right: 5px;"/>
                        Connect Project
                      </span>
          </ButtonElement>
        </template>
      </div>
      <JiraCloudDescription v-if="integrationType.type === 'JIRA'"/>
      <FigmaDescription v-if="integrationType.type === 'FIGMA'"/>
      <AzureBoardsDescription v-if="integrationType.type === 'AZURE'"/>
      <GoogleDriveDescription v-if="integrationType.type === 'GOOGLE_DRIVE'"/>
    </div>
  </ContentTemplate>
</template>


<script lang="ts">
import {defineComponent} from 'vue'
import {IntegrationNs, type PopupNs, SettingNs, WorkspaceNs} from '@shared/types'
import JiraCloudDescription
  from '@domains/settings/views/integrations/jira-cloud/jira-cloud-description.vue'
import IntegrationSkeletonComponent
  from '@domains/settings/views/integrations/integrations-skeleton.component.vue'
import IntegrationSkeletonLoadingComponent
  from '@domains/settings/views/integrations/integrations-skeloton-loading.component.vue'
import {useWorkspaceStore} from "@shared/stores/workspace.store";
import {useIntegrationStore} from "@domains/settings/stores/integration.store";
import {ButtonElement, ContentTemplate, IntegrationConfigWidget} from "@shared/components";
import {IntegrationTypesConstants} from "@shared/constants";
import {useGeneralStore} from "@shared/stores/general.store";
import FigmaDescription from "@domains/settings/views/integrations/figma/figma-description.vue";
import AzureBoardsDescription from "@domains/settings/views/integrations/azure-boards/azure-boards-description.vue";
import GoogleDriveDescription from "@domains/settings/views/integrations/google-drive/google-drive-description.vue";

export default defineComponent({
  name: 'IntegrationsDetail',
  components: {
    FigmaDescription,
    AzureBoardsDescription,
    GoogleDriveDescription,
    IntegrationSkeletonComponent,
    IntegrationSkeletonLoadingComponent,
    ContentTemplate,
    IntegrationConfigWidget,
    JiraCloudDescription,
    ButtonElement
  },
  setup() {
    // initialize the store
    const workspaceStore = useWorkspaceStore()
    const integrationStore = useIntegrationStore()
    const generalStore = useGeneralStore()
    return {workspaceStore, integrationStore, generalStore}
  },
  computed: {
    integrationDetail(): IntegrationNs.IntegrationInfo {
      return this.integrationStore.getIntegrationDetail
    },
    selectedWorkspace(): WorkspaceNs.Workspace {
      return this.workspaceStore.selectedWorkspace
    }
  },
  data() {
    return {
      showSkeleton: true,
      integrationType: {title: '', icon: ''} as IntegrationNs.IntegrationType
    }
  },
  beforeMount() {
    const integrationType = (this.$route.params.integration as string).toUpperCase()

    if (integrationType !== IntegrationNs.IntegrationTypeEnums.JIRA && integrationType !== IntegrationNs.IntegrationTypeEnums.FIGMA && integrationType !== IntegrationNs.IntegrationTypeEnums.AZURE && integrationType !== IntegrationNs.IntegrationTypeEnums.GOOGLE_DRIVE) {
      this.$router.push({
        name: SettingNs.SettingRouteEnums.INTEGRATIONS
      })
    } else {
      const workspaceId = this.selectedWorkspace.id
      this.integrationType = IntegrationTypesConstants[integrationType]

      this.integrationStore.loadIntegratedProjects({
        workspaceId,
        integrationType
      } as IntegrationNs.IntegrationInfoRequest)
      .then(() => {
        this.showSkeleton = false
      }).catch(() => {
        this.$router.push({
          name: SettingNs.SettingRouteEnums.INTEGRATIONS
        })
      })
    }
  },
  methods: {
    addIntegration() {
      const type = this.integrationDetail.integrationType
      if (type === IntegrationNs.IntegrationTypeEnums.AZURE) {
        this.$router.push('azure-boards/integration')
      } else {
        this.$router.push(`${type.toLowerCase()}/integration`)
      }
    },

    removeIntegration() {
      this.integrationStore.removeIntegration({
        workspaceId: this.selectedWorkspace.id,
        integrationType: this.integrationDetail.integrationType
      } as IntegrationNs.IntegrationInfoRequest)
      .then(() => {
        this.$router.push({
          name: SettingNs.SettingRouteEnums.INTEGRATIONS
        })
      })
    },

    configureIntegration() {
      const payload = {
        workspaceId: this.selectedWorkspace.id,
        integrationType: this.integrationDetail.integrationType
      }

      this.integrationStore.getAuthorizeUrl(payload)
      .then((response) => window.location.href = response)
      .catch((error) => console.log(error));
    }
    ,
    removeProjectIntegration(project: IntegrationNs.ProjectIntegrationInfo) {
      const payload = {
        workspaceId: this.selectedWorkspace.id,
        integrationType: this.integrationDetail.integrationType,
        integrationId: project.projectIntegrationId,
        intProjectKey: project.intProjectKey
      } as IntegrationNs.RemoveIntegrationRequest;

      this.integrationStore.removeProjectIntegration(payload)
      .then(() => {
        const payload2 =
            {
              workspaceId: this.selectedWorkspace.id,
              integrationType: this.integrationDetail.integrationType
            } as IntegrationNs.IntegrationInfoRequest;

        this.integrationStore.loadIntegratedProjects(payload2).then(() => {
        })
      })
      .catch((error) => console.log(error));
    },

    openConfirmationPopupForRemoveIntegration(): void {
      const payload = {
        content: {
          title: 'Workspace Integration',
          message: `<p>Confirm removal of <b>${this.integrationDetail.integrationType} Integration</b> from the workspace?</p>
                    <p>All existing project connections will be removed. Associated data will remain intact.</p>
                    `
        },
        callback: {
          content: 'Delete',
          function: () => this.removeIntegration()
        }
      } as PopupNs.Popup

      this.generalStore.showPopup(payload)
    },

    openConfirmationPopupForRemoveProjectIntegration(project: IntegrationNs.ProjectIntegrationInfo): void {
      const payload = {
        content: {
          title: 'Disconnect',
          message: `
                    <p>Confirm disconnection of <b>${this.integrationDetail.integrationType}</b> from <b>${project.fpProjectName}</b>?</p>
                    <p>All groups, features, and subfeatures will lose connection to Jira.</p>
                    <p><b>Data within metastory AI will remain unchanged.</b></p>
                    `
        },
        callback: {
          content: 'Disconnect',
          function: () => this.removeProjectIntegration(project)
        }
      } as PopupNs.Popup

      this.generalStore.showPopup(payload)
    }
  }
})
</script>

<style lang="scss">
.integration-config-template {
  width: 430px;
}

.add-integration {
  margin: var(--space-16) var(--space-0);
  border: 1px dashed var(--border-neutral);
  border-radius: $br-sm;
  font-size: var(--fs-14);
}

.integration-content {
  max-width: 450px;
  margin-left: var(--space-32);
  margin-top: var(--space-32);

  &-card {
    background-color: var(--card-bg);
    border: 1px solid var(--card-border);
    box-shadow: var(--card-shadow);
    padding: var(--space-16) var(--space-16) var(--space-32);
    border-radius: $br-sm;


    header {
      display: flex;
      align-items: center;

      img {
        margin-right: var(--space-8);
      }

      .header-text {
        margin: var(--space-8) var(--space-0) var(--space-8)var(--space-8);

        h4 {
          margin-bottom: var(--space-4);
        }

        h5 {
          margin-bottom: var(--space-0);
          color: var(--text-neutral);
          font-weight: 500;
        }
      }
    }

    &-body {
      margin-top: var(--space-8);

      h5 {
        color: var(--text-dark);
        font-weight: 600;
      }

      b{
        font-weight: 500;
      }

      * {
        font-size: var(--fs-14);
        font-weight: normal;
        color: var(--text-dark);
      }

      div {
        margin-top: var(--space-8);

        span {
          margin-left: var(--space-4);
          margin-right: var(--space-4);
          width: 5px;
          height: 5px;
          border-radius: $br-circle;
          display: inline-block;
          position: relative;
          top: var(--space-negative-2)
        }
      }

      .description {
        font-weight: 500;
      }

      ul{
        padding-left: var(--space-24);
        display: flex;
        flex-direction: column;
        gap: var(--space-16);
        li{
          line-height: 20px;
        }
      }
    }
  }
}

@media only screen and (max-width: 768px) {
  .integration-config-template {
    max-width: 100%;
  }
  .integration-content {
    width: 100%;
    margin-left: var(--space-0);
  }
}

.integration-headline {
  margin-bottom: var(--space-16);
  font-size: var(--fs-14);
  color: var(--text-neutral);
  font-weight: 500;
}

.jira-feature-headline {
  margin-bottom: 10px;
  margin-top: 20px;

  .jfh-col {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    background-color: var(--card-bg);
    border: 1px solid var(--card-border);
    box-shadow: var(--card-shadow);
    color: var(--text-dark);
    height: 40px;
    border-radius: $br-sm;
    font-size: var(--fs-12);
    font-weight: 600;
    padding: var(--space-0) var(--space-16);
    margin-bottom: var(--space-8);

    &.metastory{
      background-color: var(--card-bg);
    }

    &.jira{
      background: var(--jira-10);
      border: 1px solid var(--jira-20);
    }

    .icon {
      margin-right: var(--space-8);
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
}

.config-footer {
  display: flex;
  justify-content: space-between;
  gap: var(--space-16);
  margin-top: var(--space-16);

  &.flex-end{
    justify-content: flex-end;
  }

  .radio-group {
    display: flex;
    gap: var(--space-8);
  }

  .button-group {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-8);
  }
}

</style>
